import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { snack } from '@innovamat/glimmer-components';
import type { ClassroomBody, GlowApiError } from '@innovamat/glow-api-client';
import {
  useClassroomQuery,
  useDeleteClassroomMutation,
  useUnlinkTeacherMutation,
} from '@innovamat/glow-api-client';

import { useUser } from '../../user-management';
import {
  deleteClassroomFromCache,
  leaveClassroomFromCache,
  updateClassroomInCache,
} from '../cache-actions';
import { updateClassroomStudentsInCache } from '../cache-actions/update-classroom-students';
import { Classroom, isJourneyClassroom } from '../modules/classroom';

import { useConfirmationModal } from './use-confirmation-modal';

function useManageClassrooms() {
  const { t } = useTranslation();
  const { user } = useUser();
  const [editClassroom, setEditClassroom] = useState<Classroom | undefined>(
    undefined
  );
  const queryClient = useQueryClient();
  const deleteClassroomMutation = useDeleteClassroomMutation();
  const unlinkMutation = useUnlinkTeacherMutation();

  const {
    requestConfirmation,
    isConfirmationOpen,
    confirmationModalProps,
    handleCloseConfirmationModal,
  } = useConfirmationModal();

  const handleDeleteClassWithConfirmation = (classroom: Classroom) => {
    const isJourney = isJourneyClassroom(classroom);

    requestConfirmation({
      title: isJourney
        ? t('classrooms.delete-journey-confirm-modal.title')
        : t('classrooms.deleteConfirmModal.title'),
      message: isJourney
        ? t('classrooms.delete-journey-confirm-modal.message', {
            name: classroom.name!,
          })
        : t('classrooms.deleteConfirmModal.message', {
            name: classroom.name!,
          }),
      onConfirm: () => handleDeleteClass(classroom),
      confirmText: t('confirmModal.action.confirmDelete'),
    });
  };

  const handleOnError = (error: GlowApiError): void => {
    if (error) {
      error.response.errors.forEach((e) => {
        if (e.extensions?.response?.['status'] === 409) {
          snack.error(t('classroom.duplicated_name'));
        } else {
          snack.error(
            'Error: ' + JSON.parse(e.extensions?.response.body).detail
          );
        }
      });
    }
  };

  const handleDeleteClass = (classroom: Classroom) => {
    const isJourney = isJourneyClassroom(classroom);

    deleteClassroomMutation.mutate(
      {
        classroomId: classroom.id!,
      },
      {
        onSuccess: () => {
          deleteClassroomFromCache({
            queryClient,
            orgId: user?.organizationId!,
            classroomId: classroom.id!,
          });
          snack.success(
            isJourney
              ? t('classrooms.delete-journey.toast.success')
              : t('classrooms.deleteConfirmModal.toast.success')
          );
          handleCloseConfirmationModal();
        },
        onError: () => {
          snack.error(
            isJourney
              ? t('classrooms.delete-journey.toast.error')
              : "Couldn't delete classroom"
          );
        },
      }
    );
  };

  const handleLeaveClassWithConfirmation = (classroom: Classroom) => {
    const isJourney = isJourneyClassroom(classroom);

    requestConfirmation({
      title: isJourney
        ? t('classrooms.leave-journey.title')
        : t('teachers.unlinkTeacher-myself'),
      message: isJourney
        ? t('classrooms.leave-journey.confirm-message', {
            classroom: classroom.name,
          })
        : t('teachers.unlinkTeacher-myself.confirm', {
            classroom: classroom.name,
          }),
      onConfirm: () => handleLeaveClass(classroom.id),
      confirmText: isJourney
        ? t('classrooms.leave-journey.confirm')
        : t('teachers.unlinkTeacher-myself.confirmUnlink'),
    });
  };

  const handleLeaveClass = (classroomId: string) => {
    unlinkMutation.mutate(
      {
        body: {
          classroomId,
          teacherId: user?.id!,
        },
      },
      {
        onSuccess: () => {
          leaveClassroomFromCache({
            queryClient,
            orgId: user?.organizationId!,
            classroomId,
          });
          snack.success(t('teachers.unlinkTeacher.toast.success'));
          handleCloseConfirmationModal();
        },
        onError: () => {
          snack.error(t('teachers.unlinkTeacher.toast.error'));
        },
      }
    );
  };

  const handleOnEditSuccess = (classroom: Classroom) => {
    const isJourney = isJourneyClassroom(classroom);

    setEditClassroom(undefined);
    snack.success(
      isJourney
        ? t('classrooms.edit-journey.toast.success')
        : t('classroom.modal.toast.success.edit')
    );

    updateClassroomInCache({
      queryClient,
      orgId: user?.organizationId!,
      region: user?.region!,
      updatedClassroom: classroom,
    });
    updateClassroomStudentsInCache({
      queryClient,
      organizationId: user?.organizationId!,
      region: user?.region!,
      updatedClassroom: classroom,
    });

    queryClient.setQueryData(useClassroomQuery.getKey({ id: classroom.id! }), {
      classroom,
    });
  };

  const confirmModalData = {
    isOpen: isConfirmationOpen,
    isPending: deleteClassroomMutation.isPending || unlinkMutation.isPending,
    onClose: handleCloseConfirmationModal,
    ...confirmationModalProps,
  };

  const editModalData = {
    show: editClassroom !== undefined,
    onClose: () => setEditClassroom(undefined),
    classroom: editClassroom,
    onError: handleOnError,
    onSuccess: (updatedClassroom: ClassroomBody) =>
      handleOnEditSuccess({
        ...editClassroom!,
        ...updatedClassroom,
      } as Classroom),
  };

  return {
    openEditModal: setEditClassroom,
    handleOnError,
    handleDeleteClassWithConfirmation,
    handleLeaveClassWithConfirmation,
    handleOnEditSuccess,
    editModalData,
    confirmModalData,
  };
}

export { useManageClassrooms };
